import { Logo } from '@/components/Logo';
import { isIosNative } from '@/renderer/utils/isIosNative';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/16/solid';
import { ArrowRightIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useRef } from 'react';

import { useSafeTopOwner } from '@/renderer/utils/safeTop';

import { SignInLink } from './SignInLink';

interface Link {
  href: string;
  text: string;
  target?: '_blank' | '_self';
  type?: 'button';
  className?: string;
}

const LINKS1: Link[] = [
  {
    href: '/about',
    text: 'About',
    target: '_self',
  },
  {
    href: '/archive',
    text: 'Results',
    target: '_self',
  },
  {
    href: '/welcome-guide',
    text: 'Welcome Guide',
    target: '_self',
  },
  {
    href: '/order',
    text: 'Order',
    target: '_self',
    className: 'lg:border-r lg:border-white/50 lg:pr-8',
  },
];

const Linky = ({ href, text, type, target, className }: Link) => (
  <a
    href={href}
    key={text}
    target={target}
    className={clsx(type === 'button' && 'w-full sm:w-auto')}
  >
    {' '}
    <li
      className={clsx(
        'flex flex-row items-center text-sm font-medium text-white hover:text-zinc-200',
        type === 'button' &&
          'group inline-flex w-full justify-center space-x-0.5 overflow-hidden rounded-sm bg-white/10 p-2 px-6 text-sm font-medium text-white ring-1 ring-white/20 transition ring-inset hover:bg-white/20 hover:text-white hover:ring-white/30 sm:w-auto',
        className,
      )}
    >
      <span>{text}</span>
      {target === '_blank' && (
        <ArrowTopRightOnSquareIcon height={16} className="ml-1 inline-block size-4" />
      )}
      {type === 'button' && (
        <>
          <ChevronRightIcon height={20} className="size-5 group-hover:hidden" />
          <ArrowRightIcon
            height={20}
            className="mx-px hidden size-5 p-px transition-transform group-hover:block"
          />
        </>
      )}
    </li>
  </a>
);

function LoggedOutLinks() {
  return (
    <>
      <Linky href="/signin" text="Sign in" target="_self" />
      <Linky href="/register" text="Get free releases" target="_self" type="button" />
    </>
  );
}

function LoggedInLinks() {
  return (
    <>
      <Linky href="/support-chat" text="Support Chat" target="_self" />
      <Linky href="/results" text="My Results" target="_self" />
      <Linky href="/release-center" text="Release Center" target="_self" type="button" />
    </>
  );
}

function LoggedOutLink() {
  return (
    <Linky
      href="/signin"
      text="Sign in"
      target="_self"
      className="relative right-10 peer-checked/header-checkbox:hidden"
    />
  );
}

function LoggedInLink() {
  return (
    <Linky
      href="/release-center"
      text="Release Center"
      target="_self"
      type="button"
      className="relative right-10 peer-checked/header-checkbox:hidden"
    />
  );
}

export default function Header({
  absolute = true,
  safeTop = true,
}: {
  absolute?: boolean;
  // Keep the nav row clear of the iOS status bar in the native shell, which
  // paints edge-to-edge (`viewport-fit=cover`; env() is 0 on web). A margin, not
  // padding, so it also moves the `absolute` variant and the row's absolutely
  // positioned menu toggles with it. Pass false when the page already renders
  // its own inset-bearing chrome above this header (the homepage banner).
  safeTop?: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  // With safeTop this header owns the status-bar inset (the mt above), so the
  // layout's <main> must not add its own (#2028).
  useSafeTopOwner(safeTop);

  return (
    <header className={absolute ? 'relative h-0' : 'bg-zinc-950'}>
      <article
        className={clsx(
          absolute && 'absolute inset-x-0 top-0',
          safeTop && 'mt-[var(--safe-top)]',
          'z-10 flex flex-col justify-center border-b border-white/10 bg-emerald-50/5 shadow-xs backdrop-blur-xs has-checked:bg-emerald-950/90 sm:flex-row',
        )}
      >
        {' '}
        <nav
          className="flex h-fit w-full max-w-5xl flex-row items-center justify-between px-4 py-2 has-checked:flex-col has-checked:items-start has-checked:justify-start 2xl:px-0"
          aria-label="Global"
        >
          <input
            ref={ref}
            id="header-checkbox"
            type="checkbox"
            className="peer/header-checkbox absolute z-[-1] m-0 border-collapse cursor-pointer border-none border-transparent bg-transparent p-0 opacity-0 lg:hidden"
            defaultChecked={false}
          />
          <label
            htmlFor="header-checkbox"
            className="absolute top-5 right-4 text-lg text-zinc-400 peer-checked/header-checkbox:hidden peer-focus-within/nav:hidden hover:text-white lg:pointer-events-none lg:hidden lg:opacity-0"
          >
            <Bars3Icon height={24} className="size-6 shrink-0 text-white" aria-hidden="true" />
          </label>
          <label
            htmlFor="header-checkbox"
            className="absolute top-5 right-4 hidden text-lg text-zinc-400 peer-checked/header-checkbox:inline-block peer-focus-within/nav:inline-block hover:text-white lg:pointer-events-none lg:hidden lg:opacity-0"
          >
            <XMarkIcon height={24} className="size-6 shrink-0 text-white" aria-hidden="true" />
          </label>
          <ul className="flex items-center space-x-8 text-sm font-medium">
            <li>
              <a href="/">
                <span className="sr-only">RAS</span>
                <Logo className="h-12 w-auto select-none" />
              </a>
            </li>
          </ul>
          <div className="peer-checked/header-checkbox:hidden lg:hidden">
            <SignInLink signedIn={<LoggedInLink />} signedOut={<LoggedOutLink />} />
          </div>
          <ul className="hidden items-baseline justify-end space-x-8 peer-checked:items-start peer-checked/header-checkbox:top-6 peer-checked/header-checkbox:flex peer-checked/header-checkbox:w-full peer-checked/header-checkbox:flex-col peer-checked/header-checkbox:gap-1 peer-checked/header-checkbox:space-x-0 peer-checked/header-checkbox:py-4 peer-checked/header-checkbox:[&>a]:block peer-checked/header-checkbox:[&>a]:w-full peer-checked/header-checkbox:[&>a]:py-2.5 peer-checked/header-checkbox:[&>a:last-child]:mt-2 lg:flex">
            {/* Hide the "Order" (web checkout) link on iOS — App Store 3.1.1. */}
            {LINKS1.filter((l) => !(isIosNative() && l.href === '/order')).map(Linky)}
            {/* <ClientOnly
              component={() => import('./SignInLink')}
              signedIn={
                <>
                  <Linky href="/release-center" text="Account" target="_self" />
                  <Linky
                    href="/release-center"
                    text="To Release center"
                    target="_self"
                    type="button"
                  />
                </>
              }
            >
              <Linky href="/signin" text="Sign in" target="_self" />
              <Linky href="/register" text="Get free releases" target="_self" type="button" />
            </ClientOnly> */}
            <SignInLink signedIn={<LoggedInLinks />} signedOut={<LoggedOutLinks />} />
          </ul>
        </nav>
      </article>
    </header>
  );
}
